import { createContext, useRef, MutableRefObject } from "react";
import { logger } from "utils/logger";

export type AudioKeepAliveContextType = ReturnType<typeof useAudioKeepAlive>;

export const AudioKeepAliveContext = createContext<AudioKeepAliveContextType>(
  {} as AudioKeepAliveContextType,
);

export function useAudioKeepAlive() {
  const htmlAudioElementRef = useRef<HTMLAudioElement>();

  const audioKeepAliveStop = () => {
    logger.info("Stop keep alive audio");
    try {
      if (htmlAudioElementRef.current) {
        htmlAudioElementRef.current.pause();
        htmlAudioElementRef.current.currentTime = 0;
        htmlAudioElementRef.current.src = "";
      }
    } catch (e) {
      logger.error("Error stopping keep alive audio", e);
    }
  };

  const audioKeepAliveStart = (
    ttsAudioElementRef?: MutableRefObject<HTMLAudioElement | undefined>,
  ) => {
    logger.info("Start keep alive audio - unlocking iOS HTML Audio");

    try {
      // Use the TTS audio element if provided, otherwise create our own
      const audioElement =
        ttsAudioElementRef?.current || htmlAudioElementRef.current || new Audio();

      if (!htmlAudioElementRef.current) {
        htmlAudioElementRef.current = audioElement;
      }

      // If TTS audio element wasn't created yet, create it now
      if (ttsAudioElementRef && !ttsAudioElementRef.current) {
        ttsAudioElementRef.current = audioElement;
      }

      // Load and play a silent audio file synchronously to unlock iOS audio
      // This MUST happen within the user gesture event handler
      audioElement.src = "/audio/start-conversation.mp3";

      // Call play() synchronously - this is critical for iOS
      const playPromise = audioElement.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            logger.info("HTML Audio unlocked successfully");
          })
          .catch((error) => {
            logger.error("Error unlocking HTML Audio", error);
          });
      }
    } catch (e) {
      logger.error("Error starting keep alive audio", e);
    }
  };

  return {
    audioKeepAliveStart,
    audioKeepAliveStop,
    htmlAudioElementRef,
  };
}
